import { dataProvider, createClient } from '@ffimnsr/refine-postgrest'
import { getPostgrestToken } from './authProvider'
import { resolveFieldKind, JSON_FIELDS, PLAIN_ID_FIELDS } from '@/resources/fieldSchema'
import { resolveFkMapping } from '@/resources/fkMappings'

const POSTGREST_URL = import.meta.env.VITE_POSTGREST_URL as string

let clientCache: ReturnType<typeof createClient> | null = null
let lastToken = ''

function getClient() {
  const token = getPostgrestToken() ?? ''
  if (!clientCache || token !== lastToken) {
    clientCache = createClient(POSTGREST_URL, {
      headers: { Authorization: `Bearer ${token}` },
    })
    lastToken = token
  }
  return clientCache
}

/**
 * Normalise create/update payloads:
 * - '' -> null for integer/datetime/FK columns (Postgres rejects "" for non-text types)
 * - JSON string -> object for JSON columns (prevents double-encoded storage)
 */
export function cleanVariables(resource: string, variables: unknown) {
  if (!variables || typeof variables !== 'object') return
  const vars = variables as Record<string, unknown>
  for (const [k, v] of Object.entries(vars)) {
    if (JSON_FIELDS[resource]?.includes(k)) {
      if (typeof v === 'string') {
        if (v.trim() === '') vars[k] = null
        else { try { vars[k] = JSON.parse(v) } catch { /* keep raw string */ } }
      }
    } else if (v === '') {
      const kind = resolveFieldKind(resource, k)
      const isFk = !PLAIN_ID_FIELDS.has(k) && !!resolveFkMapping(k)
      if (kind === 'number' || kind === 'datetime' || isFk) vars[k] = null
    }
  }
}

/** Tables without TimestampMixin — must not receive created_at/updated_at. */
const NO_TIMESTAMP_TABLES = new Set([
  'admin_auth_audit',
  'role_type_definitions',
  'linkedin_search_cache',
])

/**
 * created_at/updated_at are NOT NULL with app-side defaults only, so direct
 * PostgREST inserts must supply them explicitly.
 */
export function injectTimestamps(resource: string, variables: unknown) {
  if (!variables || typeof variables !== 'object' || NO_TIMESTAMP_TABLES.has(resource)) return
  const vars = variables as Record<string, unknown>
  const now = new Date().toISOString()
  vars.created_at ??= now
  vars.updated_at ??= now
}

/**
 * Requisition-owned child tables (keyed by requisition_id) with no descendants of
 * their own — safe to remove when deleting the job request.
 *
 * position_assigned_users / position_questions / position_eliminating_questions
 * are ALSO used by company & org-chart positions, and moved-to-position rows may
 * still carry the source requisition_id. nullColumns guard the delete to only
 * requisition-scoped rows (position_id/org_chart_position_id IS NULL), so rows
 * belonging to a created position are never touched. company_positions and
 * org_chart_positions themselves are intentionally NOT included: deleting them
 * would cascade into real position data (applications, interviews, ...).
 */
const REQUISITION_CHILD_TABLES: Array<{ table: string; nullColumns: string[] }> = [
  { table: 'approval_requests', nullColumns: [] },
  { table: 'position_assigned_users', nullColumns: ['position_id', 'org_chart_position_id'] },
  { table: 'position_eliminating_questions', nullColumns: ['position_id'] },
  { table: 'position_questions', nullColumns: ['position_id'] },
]

/**
 * App-side cascade for the admin panel: delete a requisition's child rows before
 * deleting the requisition itself (DB FKs on requisition_id are RESTRICT).
 *
 * Uses a single filtered DELETE per table (no read-then-delete) so it works even
 * when the role can't SELECT those tables, and reports how many rows were removed.
 * Throws on server-side denial (missing grant / RLS policy) instead of silently
 * skipping and surfacing a misleading FK error afterwards.
 */
async function deleteRequisitionChildren(requisitionId: unknown): Promise<number> {
  let removed = 0
  for (const { table, nullColumns } of REQUISITION_CHILD_TABLES) {
    let query = getClient().from(table).delete().eq('requisition_id', requisitionId)
    for (const col of nullColumns) {
      query = query.is(col, null)
    }
    const { data, error } = await query.select('id')
    if (error) throw error
    removed += (data ?? []).length
  }
  return removed
}

function createDynamicDataProvider() {
  const handler: ProxyHandler<object> = {
    get(_target, prop: string | symbol) {
      if (typeof prop === 'symbol') return undefined
      // Refresh client in case token changed (e.g. after refresh)
      const provider = dataProvider(getClient())
      const fn = provider[prop as keyof typeof provider]
      if (typeof fn !== 'function') return fn
      return async (...args: unknown[]) => {
        const fixArrays = (obj: unknown) => {
          if (!obj || typeof obj !== 'object') return
          for (const [k, v] of Object.entries(obj as Record<string, unknown>)) {
            if (k === 'prohibited_role_types' && typeof v === 'string') {
              try { (obj as Record<string, unknown>)[k] = JSON.parse(v) } catch { (obj as Record<string, unknown>)[k] = [] }
            } else if (v && typeof v === 'object' && !Array.isArray(v)) {
              fixArrays(v)
            }
          }
        }
        for (const a of args) { fixArrays(a) }
        if (prop === 'create' || prop === 'update') {
          const a = args[0] as { resource?: string; variables?: unknown }
          if (a?.resource) cleanVariables(a.resource, a.variables)
        }
        if (prop === 'create') {
          const a = args[0] as { resource?: string; variables?: unknown }
          if (a?.resource) injectTimestamps(a.resource, a.variables)
        }
        if (prop === 'deleteOne') {
          const a = args[0] as { resource?: string; id?: unknown }
          if (a?.resource === 'requisitions' && a?.id != null) {
            const removed = await deleteRequisitionChildren(a.id)
            try {
              return await fn(...args)
            } catch (err) {
              const details = (err as { details?: string })?.details ?? ''
              const blockedChild = REQUISITION_CHILD_TABLES.find(({ table }) =>
                details.includes(`"${table}"`),
              )
              if (removed === 0 && blockedChild) {
                throw new Error(
                  `Server denied deleting the linked ${blockedChild} rows. ` +
                    'Grant the postgrest role DELETE on that table (or add an RLS policy), then retry.',
                )
              }
              throw err
            }
          }
        }
        return fn(...args)
      }
    },
    has(_target, prop) {
      return ['getList', 'getOne', 'getMany', 'create', 'createMany', 'update', 'updateMany', 'deleteOne', 'deleteMany', 'getApiUrl', 'custom'].includes(prop as string)
    },
  }
  return new Proxy({}, handler) as ReturnType<typeof dataProvider>
}

export const postgrestDataProvider = createDynamicDataProvider()
